import { createCreatorProductStore } from '@services/MarppleShopStudio/widget/CreatorProduct/core/store/createStore.ts';
import { MODE } from '@services/MarppleShopStudio/widget/CreatorProduct/core/types/types.ts';
import { View } from 'rune-ts';
import {
  StockInput,
  stockInputFactory,
} from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_Stock/StockInputFactory.ts';
import { BaseSection } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/section-views/BaseSection.ts';
import {
  ChangeSpoItemValue,
  DeleteSpoItems,
} from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_Stock/events.ts';
import { formKeyMap } from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/shared.ts';
import {
  ApplyNewSpoItems,
  Input_AddOption,
} from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_AddOption/Input_AddOption.ts';
import { StockType } from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/constants.ts';
import {
  toSpoItems,
  extractSpoOptions,
  updateSpoItems,
} from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/section-views/helper.ts';
import { Input_StockThresholdSelector } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_StockThresholdSelector.ts';

interface StockAndOptionSectionData<T extends MODE> {
  store: ReturnType<typeof createCreatorProductStore<T>>;
}

export class StockAndOptionSection<T extends MODE> extends BaseSection<T, StockAndOptionSectionData<T>> {
  protected readonly sectionTitle = '판매 정보';
  private store = this.data.store;

  protected override inputViews(): Array<View> {
    return [
      this.createStockInput(),
      new Input_AddOption({
        name: formKeyMap.spo_items,
        value: this.store.formData._.product._.spo._.spo_options,
        title: '옵션 추가',
      }),
      new Input_StockThresholdSelector({
        name: formKeyMap.low_stock_threshold,
        value: this.store.formData.meta.low_stock_threshold ?? null,
        title: '품절임박 표시',
        required: true,
        setField: this.store.actions.setField,
      }),
    ];
  }

  protected override onRender() {
    this.addEventListener(ChangeSpoItemValue, this.changeSpoItemValue);

    this.addEventListener(ApplyNewSpoItems, this.applyNewSpoItems);

    this.addEventListener(DeleteSpoItems, this.deleteSpoItems);
  }

  private changeSpoItemValue(e: ChangeSpoItemValue) {
    /* spo_items 값 변경 */
    this.store.actions.setField(
      formKeyMap.spo_items,
      updateSpoItems(this.store.formData._.product._.spo._.spo_items, e.detail.stock_values),
    );
  }

  private applyNewSpoItems(e: ApplyNewSpoItems) {
    /* spo_options 갱신 */
    this.store.actions.setField(formKeyMap.spo_options, e.detail.spo_options);

    const error = this.store.errors[formKeyMap.spo_options];
    if (error) {
      this.subView(Input_AddOption)?.setError(true, error);
      return;
    }

    /* spo_items 갱신 */
    this.store.actions.setField(
      formKeyMap.spo_items,
      toSpoItems(this.store.formData._.product._.spo._.spo_options),
    );

    /* stock_type 갱신 */
    this.store.actions.setField(
      formKeyMap.stock_type,
      this.store.formData._.product._.spo._.spo_items.length ? StockType.OPTION_STOCK : StockType.TOTAL_STOCK,
    );
    this.redraw();
  }

  private deleteSpoItems(e: DeleteSpoItems) {
    /* spo_items 삭제 */
    const deleteIndexSet = new Set(e.detail.delete_target_indexes);
    this.store.actions.setField(
      formKeyMap.spo_items,
      this.store.formData._.product._.spo._.spo_items.filter((_, i) => !deleteIndexSet.has(i)),
    );

    /* spo_options 갱신 */
    this.store.actions.setField(
      formKeyMap.spo_options,
      extractSpoOptions(
        this.store.formData._.product._.spo._.spo_items,
        this.store.formData._.product._.spo._.spo_options,
      ),
    );

    /* stock_type 갱신 */
    this.store.actions.setField(
      formKeyMap.stock_type,
      this.store.formData._.product._.spo._.spo_items.length ? StockType.OPTION_STOCK : StockType.TOTAL_STOCK,
    );

    this.redraw();
  }

  private createStockInput(): StockInput {
    const mode = this.store.mode;
    const spo = this.store.formData._.product._.spo;
    const stockType = spo.stock_type;

    return stockInputFactory.create(mode, spo, stockType);
  }
}
